import { ColumnDef } from "@tanstack/react-table";
import Badge from "components/base/Badge";
import { Link } from "react-router-dom";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

// Definição do tipo para a tabela de usuários
export interface UsuarioTableType {
    id: number;
    codigo: number;
    nome: string;
    email: string;
    grupo?: string;
    ultimoAcesso?: string;
    ativo?: boolean;
    imageUrl?: string;
    isDeleted?: boolean;
    deletedAt?: string;
    telefone?: string;
}

// Componente para exibir avatar do usuário
const UserAvatar = ({ imageUrl, nome, isDeleted }: { imageUrl?: string; nome: string; isDeleted?: boolean }) => {
    const nameInitial = nome ? nome.charAt(0) : '';
    const size = 40; // aumento do tamanho do avatar de 32 para 40 pixels

    const containerStyles = {
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isDeleted ? 0.6 : 1, // Diminui opacidade se estiver deletado
        filter: isDeleted ? 'grayscale(100%)' : 'none', // Aplica filtro grayscale se deletado
    };

    return (
        <div style={containerStyles}>
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={nome}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover' }}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = nameInitial.toUpperCase();
                    }}
                />
            ) : (
                <div
                    className="w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: 'var(--phoenix-primary-bg-subtle)',
                        color: 'var(--phoenix-avatar-name-color)',
                        fontSize: `${size / 2}px`,
                        fontWeight: 'bold'
                    }}
                >
                    {nameInitial ? nameInitial.toUpperCase() : <FontAwesomeIcon icon={faUser} />}
                </div>
            )}
        </div>
    );
};

export const usuarioTableColumns: ColumnDef<UsuarioTableType>[] = [
    {
        accessorKey: "codigo",
        header: "Código",
        meta: {
            headerProps: { style: { width: 80 }, className: "ps-4" },
            cellProps: {
                className: "fs-9 fw-semibold ps-4 text-body-tertiary text-opacity-85",
            },
        },
    },
    {
        accessorKey: "nome",
        header: "Usuário", // Mudado de "Nome" para "Usuário"
        cell: ({ row: { original } }) => {
            const { nome, id, imageUrl, isDeleted, deletedAt } = original;
            return (
                <div className="d-flex align-items-center">
                    <UserAvatar imageUrl={imageUrl} nome={nome} isDeleted={isDeleted} />
                    <div className="ms-2">
                        <Link
                            to={`/cadastros/usuarios/${id}`}
                            className={`fw-semibold ${isDeleted ? 'text-decoration-line-through text-muted' : ''}`}
                        >
                            {nome}
                        </Link>
                        {isDeleted && deletedAt && (
                            <div className="small text-danger-emphasis">
                                Excluído em {format(new Date(deletedAt), 'dd/MM/yyyy HH:mm')}
                            </div>
                        )}
                    </div>
                </div>
            );
        },
        meta: {
            headerProps: { style: { width: 250 }, className: "ps-4" },
            cellProps: { className: "ps-4" },
        },
    },
    {
        accessorKey: "email",
        header: "E-mail",
        cell: ({ getValue, row: { original } }) => {
            const email = getValue() as string;
            const { isDeleted } = original;
            return <span className={`text-nowrap ${isDeleted ? 'text-muted' : ''}`}>{email}</span>;
        },
    },
    {
        accessorKey: "telefone",
        header: "Telefone",
        cell: ({ getValue, row: { original } }) => {
            const telefone = getValue() as string;
            const { isDeleted } = original;
            if (!telefone) {
                return <span className={`text-nowrap ${isDeleted ? 'text-muted' : ''}`}>Não informado</span>;
            }
            return <span className={`text-nowrap ${isDeleted ? 'text-muted' : ''}`}>{telefone}</span>;
        },
    },
    {
        accessorKey: "grupo",
        header: "Perfil",
        cell: ({ getValue, row: { original } }) => {
            const grupo = getValue() as string;
            const { isDeleted } = original;
            return <span className={`text-nowrap ${isDeleted ? 'text-muted' : ''}`}>{grupo}</span>;
        },
    },
    {
        accessorKey: "status",
        header: "Status",
        cell: ({ row: { original } }) => {
            const { isDeleted } = original;

            if (isDeleted) {
                return <Badge bg="danger" className="badge-subtle rounded-pill">Excluído</Badge>;
            }

            return <Badge bg="success" className="badge-subtle rounded-pill">Ativo</Badge>;
        },
    },
];

const UsuarioTable = () => {
    return (
        <div>
            <AdvanceTable tableProps={{ className: "phoenix-table fs-9" }} />
            <AdvanceTableFooter pagination showViewAllBtn={false} />
        </div>
    );
};

export default UsuarioTable;